import { useCallback, useMemo } from "react";

import {
  ProductDataRemovableProductCodeLiterals,
  ProductDataRemovalTask,
} from "../../../../../../application/domain/product-data-removal-task";
import {
  useApproveProductDataRemovalTask,
  useCancelProductDataRemovalTask,
  useCreateProductDataRemovalTask,
  useFetchOrgData,
  useFetchOrgProducts,
  useFetchProductDataRemovalTasks,
} from "./api-hooks";
import { validateCreateTask } from "../../../../../../application/domain/product-data-removal-task/validation";

export const useProductDataRemovalTasks = (orgId: string) => {
  /*
   * Queries
   */
  const orgData = useFetchOrgData(orgId);
  const orgProducts = useFetchOrgProducts(orgId);
  const smartApplyTasks = useFetchProductDataRemovalTasks(orgId, "SmartApply");
  const surveyTasks = useFetchProductDataRemovalTasks(orgId, "Survey");

  const refetchAll = useCallback(() => {
    smartApplyTasks.refetch();
    surveyTasks.refetch();
  }, [smartApplyTasks, surveyTasks]);

  /*
   * Validations
   */

  const validateCreateSmartApplyTask = useMemo<{
    valid: boolean;
    errorMessage: string;
  }>(() => {
    return validateCreateTask(
      "SmartApply",
      orgProducts.data,
      smartApplyTasks.data,
    );
  }, [orgProducts.data, smartApplyTasks.data]);

  const validateCreateSurveyTask = useMemo<{
    valid: boolean;
    errorMessage: string;
  }>(() => {
    return validateCreateTask("Survey", orgProducts.data, surveyTasks.data);
  }, [orgProducts.data, surveyTasks.data]);

  /*
   * Mutations
   */
  const createTaskMutation = useCreateProductDataRemovalTask();
  const createTask = useCallback(
    async (
      targetType: ProductDataRemovableProductCodeLiterals,
      dueAt: Date,
    ) => {
      await createTaskMutation.trigger(orgId, targetType, { dueAt });
      refetchAll();
    },
    [orgId, createTaskMutation, refetchAll],
  );

  const approveTaskMutation = useApproveProductDataRemovalTask();
  const approveTask = useCallback(
    async (task: ProductDataRemovalTask) => {
      await approveTaskMutation.trigger(task);
      refetchAll();
    },
    [approveTaskMutation, refetchAll],
  );

  const cancelTaskMutation = useCancelProductDataRemovalTask();
  const cancelTask = useCallback(
    async (task: ProductDataRemovalTask) => {
      await cancelTaskMutation.trigger(task);
      refetchAll();
    },
    [cancelTaskMutation, refetchAll],
  );

  /*
   * States
   */
  const isLoading = useMemo(
    () =>
      orgProducts.isLoading ||
      smartApplyTasks.isLoading ||
      surveyTasks.isLoading,
    [orgProducts.isLoading, smartApplyTasks.isLoading, surveyTasks.isLoading],
  );
  const isMutating = useMemo(
    () =>
      createTaskMutation.isMutating ||
      approveTaskMutation.isMutating ||
      cancelTaskMutation.isMutating,
    [
      createTaskMutation.isMutating,
      approveTaskMutation.isMutating,
      cancelTaskMutation.isMutating,
    ],
  );

  return {
    orgData: orgData.data,
    smartApplyTasks: smartApplyTasks.data,
    surveyTasks: surveyTasks.data,
    validateCreateSmartApplyTask,
    validateCreateSurveyTask,
    createTask,
    approveTask,
    cancelTask,
    isLoading,
    isMutating,
  };
};
